import { defineStore } from 'pinia'
import type { AccountInfo } from '@azure/msal-browser'
import * as Sentry from '@sentry/vue'
import { MsalAuthService } from '@/core/modules/auth/MsalAuthService'
import { AdminUserDto } from '@swipe4work/models-orval'
import { createAdminUser, listAdminUsers, syncMyInternalRoles } from '@swipe4work/api-orval'
import posthog from 'posthog-js'
import { useAdminAccessStore } from '@/shared/stores/AdminAccessStore'

/**
 * Canonical signature of a set of "Department::Name" role strings: trimmed, de-duplicated and sorted so that
 * two sets with the same members compare equal regardless of order or duplicates.
 */
function roleSignature(roles: string[]): string {
  return [ ...new Set(roles.map(r => r.trim()).filter(Boolean)) ].sort().join('|')
}

export const useAuthStore = defineStore('auth',{
  state: () => ({
    user: null as AccountInfo | null,
    authService: null as unknown as MsalAuthService,
    adminUsers: undefined as AdminUserDto[] | undefined,

    isUserAuthenticated: false as boolean,
    isUserCreated: false as boolean,
  }),
  actions: {
    async init() {
      if (this.authService) {
        return
      }

      this.authService = new MsalAuthService()

      await this.authService.init()

      if (!await this.isAuthenticated()) {
        return
      }

      this.userInfo()

      await this.getAccessToken()

      /*
       * Provision the admin user for the signed-in Entra identity (idempotent). This used to run
       * in login() after the popup resolved; now that sign-in is a top-level redirect, login()
       * navigates away before it could run, so it happens here on the authenticated return.
       */
      if (!this.isUserCreated) {
        try {
          await createAdminUser()
          this.isUserCreated = true
        } catch (e) {
          Sentry.captureException(e)
        }
      }

      // Load the internal admin access (roles/permissions/global flag) for permission-gated pages.
      await useAdminAccessStore().init()

      // Reconcile the database roles with the roles carried in the access token before the app renders.
      await this.reconcileInternalRoles()
    },
    /**
     * Compares the internal roles in the access token against the roles returned by GetAdminAccess (the
     * database). On a mismatch, asks the backend to sync the database roles from the token and reloads the
     * page so everything loads with the correct permissions. A sessionStorage guard prevents a reload loop
     * when a token role has no matching database role (it can never appear in GetAdminAccess).
     */
    async reconcileInternalRoles() {
      try {
        const account = this.user
        if (!account) {
          return
        }

        // App-role assignments are emitted into the ID token claims by Entra ID.
        const claims = account.idTokenClaims as { roles?: string[] } | undefined
        const tokenRoles = roleSignature(claims?.roles ?? [])

        const dbRoles = roleSignature(
          (useAdminAccessStore().access?.roles ?? []).map(r => `${r.department}::${r.name}`),
        )

        // Already in sync — nothing to do.
        if (tokenRoles === dbRoles) {
          return
        }

        const guardKey = `internal-roles-sync:${account.localAccountId}:${import.meta.env.VITE_ENV}`
        if (sessionStorage.getItem(guardKey) === tokenRoles) {
          /*
           * Already attempted a sync for exactly these token roles this session; a remaining mismatch means
           * a token role has no database counterpart. Don't reload again.
           */
          return
        }
        sessionStorage.setItem(guardKey, tokenRoles)

        await syncMyInternalRoles()
        window.location.reload()
      } catch (e) {
        // A sync failure must not block login.
        Sentry.captureException(e)
      }
    },
    userInfo() {
      if (!this.authService) {
        return
      }
      this.user = this.authService.user() as AccountInfo | null

      if (!this.user) {
        return
      }

      Sentry.setUser({
        id: this.user.localAccountId,
        username: this.user.username,
      })

      posthog.identify(this.user.localAccountId)

      return this.user
    },
    async isAuthenticated() {
      const authenticated = await this.authService.isAuthenticated()

      this.isUserAuthenticated = authenticated

      if (!authenticated) {
        return false
      }

      return true
    },
    async getAccessToken() {
      if (!this.authService) {
        return ''
      }

      return await this.authService.getAccessToken() ?? ''
    },
    async login() {
      /*
       * Navigates to Entra and back; the session (user provisioning, roles) is finalized
       * by init() on the authenticated return.
       */
      await this.authService.login()
    },
    async logout() {
      await this.authService.logout()

      Sentry.setUser(null)
      posthog.reset()
    },
    async getAdminUsers() {
      if (this.adminUsers !== undefined) {
        return this.adminUsers
      }

      const res = await listAdminUsers()

      return this.adminUsers = res.users
    },
  },
})
